import KitModal from '@omni/kit/components/KitModalV2';
import {
  PositionAnchor,
  TargetProps,
} from '@omni/kit/components/Popover/types';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Depth from '@omni/kit/theming/Depth';
import Spacing from '@omni/kit/theming/Spacing';
import React, { ReactElement, useEffect } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import useAnimatedPositions from './useAnimatedPositions';

const DEFAULT_MODAL_WIDTH = 248;
const DEFAULT_MODAL_HEIGHT = 118;

interface IProps {
  children?: ReactElement;
  height?: number;
  isOpen?: boolean;
  anchorBottom?: boolean;
  onClose?: () => void;
  position?: PositionAnchor;
  targetOffset?: number;
  width?: number;
  targetRef: TargetProps;
}

export default ({
  children,
  height = DEFAULT_MODAL_HEIGHT,
  isOpen = false,
  anchorBottom = false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClose = () => {},
  position,
  targetRef,
  width = DEFAULT_MODAL_WIDTH,
}: IProps): ReactElement => {
  const insets = useSafeAreaInsets();
  const { left, top } = useAnimatedPositions({
    targetRef,
    height,
    width,
    position,
  });

  /**
   * Setting overflow to hidden to stop webshell from scrolling.
   * Could potentially expose a dispatch event to toggle scrolling in webshell.
   */
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    if (isOpen && anchorBottom) {
      window.document.body.style.overflow = 'hidden';
    } else {
      window.document.body.style.overflow = 'visible';
    }

    return () => {
      window.document.body.style.overflow = 'visible';
    };
  }, [isOpen, anchorBottom]);

  return (
    <View style={{ zIndex: anchorBottom ? 100 : 1 }}>
      <KitModal
        anchorBottom={anchorBottom}
        backdropOpacity={anchorBottom ? 0.7 : 0}
        isVisible={isOpen}
        modalStyle={{
          ...Depth.l,
          ...styles.modal,
          ...(anchorBottom
            ? {
                paddingBottom: insets.bottom + Spacing.l,
              }
            : {
                height,
                left,
                position: 'absolute',
                top,
                width,
              }),
        }}
        onClose={onClose}
      >
        {children}
      </KitModal>
    </View>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: Colors.N0,
    borderTopLeftRadius: BorderRadius.l,
    borderTopRightRadius: BorderRadius.l,
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    width: '100%',
  },
});
